import {
  getList,
  getInt32,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { OptionV1, OptionV1DTO } from "./optionV1";

export interface MultiChoiceQuestionV1DTO {
  options: OptionV1DTO[];
  maxSelectionAllowed: number;
  isChoiceResetAllowed?: boolean | null;
  isUnanswered?: boolean | null;
}

export class MultiChoiceQuestionV1 {
  readonly isMultiChoiceQuestionV1MultiChoiceQuestionV1: boolean = true;

  readonly options: OptionV1[];
  readonly maxSelectionAllowed: number;
  readonly isChoiceResetAllowed: boolean | null;
  readonly isUnanswered: boolean | null;

  static readonly constraints = {
    "options": {
      "minSize": 1,
      "maxSize": 32,
    },
    "maxSelectionAllowed": {
      "minValue": 1,
      "maxValue": 32,
    },
  }

  constructor(
  options: OptionV1[], 
  maxSelectionAllowed: number, 
  isChoiceResetAllowed: boolean | null = null, 
  isUnanswered: boolean | null = null
  ) {
    MultiChoiceQuestionV1.validateOptions(options);
    this.options = options;
    MultiChoiceQuestionV1.validateMaxSelectionAllowed(maxSelectionAllowed);
    this.maxSelectionAllowed = maxSelectionAllowed;
    this.isChoiceResetAllowed = isChoiceResetAllowed;
    this.isUnanswered = isUnanswered;
  }

  static fromDTO(dto: MultiChoiceQuestionV1DTO): MultiChoiceQuestionV1 {
    const options = getList<OptionV1DTO, OptionV1>(
      dto,
      "options",
      OptionV1.fromDTO,
      InvalidMultiChoiceQuestionV1OptionsError
    );
    const maxSelectionAllowed = getInt32(dto, "maxSelectionAllowed", InvalidMultiChoiceQuestionV1MaxSelectionAllowedError);
    const isChoiceResetAllowed = getOptionalBoolean(dto, "isChoiceResetAllowed", InvalidMultiChoiceQuestionV1IsChoiceResetAllowedError);
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidMultiChoiceQuestionV1IsUnansweredError);
    return new MultiChoiceQuestionV1(
      options,
      maxSelectionAllowed,
      isChoiceResetAllowed,
      isUnanswered
    );
  }

  toDTO(): MultiChoiceQuestionV1DTO {
    const dto: MultiChoiceQuestionV1DTO = {
        options: this.options.map((e: OptionV1) => { return e.toDTO() }),
        maxSelectionAllowed: this.maxSelectionAllowed,
    };
    if (this.isChoiceResetAllowed !== undefined && this.isChoiceResetAllowed !== null){
      dto.isChoiceResetAllowed = this.isChoiceResetAllowed;
    }
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  private static validateOptions = (options: OptionV1[]): void => {
    if(options.length < 1) {
      throw new InvalidMultiChoiceQuestionV1OptionsMinSizeError(
        1,
        `Attribute options is too short. Size is ${options.length}. Min size is 1.`
      );
    }
    if(options.length > 32) {
      throw new InvalidMultiChoiceQuestionV1OptionsMaxSizeError(
        32,
        `Attribute options is too long. Size is ${options.length}. Max size is 32.`
      );
    }
  }
  private static validateMaxSelectionAllowed = (maxSelectionAllowed: number): void => {
    if(maxSelectionAllowed < 1) {
      throw new InvalidMultiChoiceQuestionV1MaxSelectionAllowedMinValueError(
        1,
        `Attribute maxSelectionAllowed has value ${maxSelectionAllowed}. Min value is 1.`
      );
    }
    if(maxSelectionAllowed > 32) {
      throw new InvalidMultiChoiceQuestionV1MaxSelectionAllowedMaxValueError(
        32,
        `Attribute maxSelectionAllowed has value ${maxSelectionAllowed}. Max value is 32.`
      );
    }
  }
  
}

MultiChoiceQuestionV1.prototype.toString = function toString(): string {
  return `MultiChoiceQuestionV1(options='${this.options}',maxSelectionAllowed='${this.maxSelectionAllowed}',isChoiceResetAllowed='${this.isChoiceResetAllowed}',isUnanswered='${this.isUnanswered}')`;
};

export class InvalidMultiChoiceQuestionV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1Error";
  }
}
export class InvalidMultiChoiceQuestionV1OptionsError extends InvalidMultiChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1OptionsError";
  }
}

export class InvalidMultiChoiceQuestionV1OptionsMaxSizeError extends InvalidMultiChoiceQuestionV1OptionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1OptionsMaxSizeError";
  }
}

export class InvalidMultiChoiceQuestionV1OptionsMinSizeError extends InvalidMultiChoiceQuestionV1OptionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1OptionsMinSizeError";
  }
}

export class InvalidMultiChoiceQuestionV1MaxSelectionAllowedError extends InvalidMultiChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1MaxSelectionAllowedError";
  }
}

export class InvalidMultiChoiceQuestionV1MaxSelectionAllowedMaxValueError extends InvalidMultiChoiceQuestionV1MaxSelectionAllowedError {
  readonly maxValue: number;
  constructor(maxValue: number, message?: string) {
    super(message);
    this.maxValue = maxValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1MaxSelectionAllowedMaxValueError";
  }
}

export class InvalidMultiChoiceQuestionV1MaxSelectionAllowedMinValueError extends InvalidMultiChoiceQuestionV1MaxSelectionAllowedError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1MaxSelectionAllowedMinValueError";
  }
}

export class InvalidMultiChoiceQuestionV1IsChoiceResetAllowedError extends InvalidMultiChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1IsChoiceResetAllowedError";
  }
}

export class InvalidMultiChoiceQuestionV1IsUnansweredError extends InvalidMultiChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidMultiChoiceQuestionV1IsUnansweredError";
  }
}
